import { ContentLayout, LoadingScreen } from '@nohara-dxdev/react-core';
import {
  UserOnboardingCheck,
  OrgSelectCheck,
  useApp,
  AppendScopeItem,
  EntityProvider,
  useOidc,
  useUserOrgs,
  useScopeIds,
} from '@nohara-dxdev/react-oidc';
import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useRouteError } from 'react-router';

import { AppLayout } from '@/components/layouts';
import { env } from '@/config/env';
import { paths } from '@/config/paths';
import { getProjectQueryOptions } from '@/features/projects/api/get-project';

import AccessDeniedRoute from '../access-denied';

export const ErrorBoundary = () => {
  const { t } = useTranslation();
  const error = useRouteError();

  console.error(error);

  return (
    <AppLayout>
      <ContentLayout title={t('common.errors.somethingWentWrong')}>Error</ContentLayout>
    </AppLayout>
  );
};

const OrgRoot = () => {
  const { data: app, isLoading } = useApp(env.APP_ID);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <AppendScopeItem type="app" id={env.APP_ID} entity={app}>
      <OrgAccessGate />
    </AppendScopeItem>
  );
};

const OrgAccessGate = () => {
  const { userId } = useOidc();
  const { data: orgs, isLoading: isLoadingOrgs } = useUserOrgs(userId);

  if (isLoadingOrgs) {
    return <LoadingScreen />;
  }

  if (!orgs || orgs.length === 0) {
    return <AccessDeniedRoute />;
  }

  return (
    <UserOnboardingCheck onboardingPath={paths.onboarding.getHref()}>
      <OrgSelectCheck
        getOrgPath={paths.org.dashboard.getHref}
        Layout={AppLayout}
        fallbackElement={<AccessDeniedRoute />}
        ignorePaths={[paths.onboarding.getHref(), paths.org.userProfile.getHref()]}
      >
        <ProjectEntityProvider>
          <Outlet />
        </ProjectEntityProvider>
      </OrgSelectCheck>
    </UserOnboardingCheck>
  );
};

const ProjectEntityProvider = ({ children }: PropsWithChildren) => {
  const { orgId } = useScopeIds();

  if (!orgId) {
    return children;
  }

  return (
    <EntityProvider
      config={{
        project: (projectId: string) => getProjectQueryOptions(orgId, projectId),
      }}
    >
      {children}
    </EntityProvider>
  );
};

export default OrgRoot;
